import { useEffect, useState } from 'react'
import { BackHandler } from 'react-native'
import { useToastController } from '@my/ui'
import { uploadImage } from 'app/lib/upload-image'
import { AuthUser } from 'app/provider/auth'
import { createCustomUuid } from 'app/utils/create-custom-uuid'
import { GroupPostEditWizard } from './group-post-edit-wizard'
import { useUpdatePost } from '../commands'
import { GroupPost } from 'app/types'
import { SubmitPostData } from './post-creation-view'
import { groupPostByIdQuery } from 'app/features/group/queries'

type GroupPostEditWizardProps = {
  open: boolean
  onOpenChange: (value) => void
  post: Pick<GroupPost, 'id' | 'text' | 'image' | 'group'>
  user: Pick<AuthUser, 'id' | 'fullName' | 'avatar' | 'token'> | null
}

export const GroupPostEditWizardContainer = (props: GroupPostEditWizardProps) => {
  const toast = useToastController()
  const [submitting, setSubmitting] = useState(false)

  const [updatePost] = useUpdatePost()

  useEffect(() => {
    const backAction = () => {
      props.onOpenChange(false);
      return props.open
    }

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => backHandler.remove();
  }, [])

  const handleSubmit = async (data: SubmitPostData) => {
    const { text, image } = data

    // TODO:
    // Support multiple images exists,
    // Just need to update the frontend
    const images = image && [image]

    if (!props.user || !props.post.id) {
      console.log("User or group doesn't exist")
      return
    }

    setSubmitting(true)
    try {
      // Upload images
      let imageIds: string[] = []
      if (images && images.length !== 0) {
        for (const image of images) {
          const imageName = createCustomUuid()
          const res = await uploadImage({
            image: image,
            name: `post-image-${props.user.id}--${imageName}`,
            authToken: props.user.token,
            collection: 'media',
          })

          if (res.doc) {
            imageIds.push(res.doc.id)
          } else {
            toast.show('Failed to upload image', {
              burntOptions: {
                preset: 'error',
              },
            })
          }
        }
      }

      await updatePost({
        variables: {
          id: props.post.id,
          text: text || '',
          imageIds,
        },
        refetchQueries: [
          {
            query: groupPostByIdQuery.query,
            variables: {
              id: props.post.id,
            },
          },
        ],
        onCompleted: async (data) => {
          if (!data?.updatePost?.post.id) {
            toast.show('Failed to update post', {
              burntOptions: {
                preset: 'error',
              },
            })
            return
          }
          setSubmitting(false)
          props.onOpenChange(false)
          toast.show('Post updated')
        },

        onError: () => {
          toast.show('Failed to update post', {
            burntOptions: {
              preset: 'error',
            },
          })
          setSubmitting(false)
        },
      })
    } catch (error) {
      toast.show('Failed to update post', {
        burntOptions: {
          preset: 'error',
        },
      })
      console.log(error)
      setSubmitting(false)
    }
  }

  if (!props.user) return null

  return (
    <GroupPostEditWizard
      post={props.post}
      user={props.user}
      open={props.open}
      submitting={submitting}
      onOpenChange={props.onOpenChange}
      onSubmit={handleSubmit}
    />
  )
}
